/**
 * Navigation Gating Configuration
 * 
 * Centralized system for sequential workflow gating in the project sidebar.
 * Enforces: Discovery → Definition → Design → Delivery → Traceability
 */

import { useMemo } from 'react';
import { useDiscovery, type DiscoveryArtefact } from '@/hooks/use-discovery';
import { useDefinition } from '@/hooks/use-definition';
import { useDesignVersionsRaw } from '@/hooks/use-design';
import { useDeliveryVersionsRaw } from '@/hooks/use-delivery';

// ============================================================================
// Types
// ============================================================================

export type Phase = 'discovery' | 'definition' | 'design' | 'delivery';

export interface GatingRule {
  id: string;
  requiredPhase: Phase | null; // null = always accessible
  tooltipWhenBlocked: string;
}

export interface PhaseStatus {
  discovery: boolean;
  definition: boolean;
  design: boolean;
  delivery: boolean;
}

export interface NavigationGatingResult {
  /** Check if a navigation item is disabled due to gating */
  isItemDisabled: (itemId: string) => boolean;
  /** Get the tooltip message for a blocked item */
  getBlockedTooltip: (itemId: string) => string;
  /** Current completion status of each phase */
  phaseStatus: PhaseStatus;
  /** Whether the gating data is still loading */
  isLoading: boolean;
}

// ============================================================================
// Gating Rules Configuration
// ============================================================================

export const NAVIGATION_GATING: Record<string, GatingRule> = {
  // Always accessible
  'chats': { id: 'chats', requiredPhase: null, tooltipWhenBlocked: '' },
  'guru': { id: 'guru', requiredPhase: null, tooltipWhenBlocked: '' },
  'knowledge': { id: 'knowledge', requiredPhase: null, tooltipWhenBlocked: '' },
  'overview': { id: 'overview', requiredPhase: null, tooltipWhenBlocked: '' },
  'discovery': { id: 'discovery', requiredPhase: null, tooltipWhenBlocked: '' },
  
  // Discovery sub-items (always accessible within Discovery)
  'discovery-goals': { id: 'discovery-goals', requiredPhase: null, tooltipWhenBlocked: '' },
  'discovery-interviews': { id: 'discovery-interviews', requiredPhase: null, tooltipWhenBlocked: '' },
  'discovery-competitors': { id: 'discovery-competitors', requiredPhase: null, tooltipWhenBlocked: '' },
  
  // Definition - requires Discovery complete
  'definition': { 
    id: 'definition', 
    requiredPhase: 'discovery',
    tooltipWhenBlocked: 'Complete Discovery first. Add at least one Goal, Interview, and Competitor.'
  },
  'definition-personas': { id: 'definition-personas', requiredPhase: 'discovery', tooltipWhenBlocked: 'Complete Discovery first.' },
  'definition-usecases': { id: 'definition-usecases', requiredPhase: 'discovery', tooltipWhenBlocked: 'Complete Discovery first.' },
  'definition-servicemap': { id: 'definition-servicemap', requiredPhase: 'discovery', tooltipWhenBlocked: 'Complete Discovery first.' },
  'definition-journeys': { id: 'definition-journeys', requiredPhase: 'discovery', tooltipWhenBlocked: 'Complete Discovery first.' },
  'definition-storyboards': { id: 'definition-storyboards', requiredPhase: 'discovery', tooltipWhenBlocked: 'Complete Discovery first.' },
  
  // Design - requires Definition complete
  'design': { 
    id: 'design', 
    requiredPhase: 'definition',
    tooltipWhenBlocked: 'Complete Definition first. Add at least one Persona and Use Case.'
  },
  'design-architecture': { id: 'design-architecture', requiredPhase: 'definition', tooltipWhenBlocked: 'Complete Definition first.' },
  'design-areas': { id: 'design-areas', requiredPhase: 'definition', tooltipWhenBlocked: 'Complete Definition first.' },
  
  // Delivery - requires Design complete
  'delivery': { 
    id: 'delivery', 
    requiredPhase: 'design',
    tooltipWhenBlocked: 'Complete Design first. Add Product Architecture or Application Areas.'
  },
  'delivery-architecture': { id: 'delivery-architecture', requiredPhase: 'design', tooltipWhenBlocked: 'Complete Design first.' },
  'delivery-storymap': { id: 'delivery-storymap', requiredPhase: 'design', tooltipWhenBlocked: 'Complete Design first.' },
  'delivery-mvp': { id: 'delivery-mvp', requiredPhase: 'design', tooltipWhenBlocked: 'Complete Design first.' },
  'delivery-stories': { id: 'delivery-stories', requiredPhase: 'design', tooltipWhenBlocked: 'Complete Design first.' },
  
  // Traceability - requires Delivery complete
  'traceability': { 
    id: 'traceability', 
    requiredPhase: 'delivery',
    tooltipWhenBlocked: 'Complete Delivery first to view requirements traceability.'
  },
};

// ============================================================================
// Phase Completion Logic
// ============================================================================

/**
 * Check if an artefact has items (at least 1 entry)
 */
function hasItems(artefact: DiscoveryArtefact | null | undefined): boolean {
  return (artefact?.items?.length ?? 0) >= 1;
}

/**
 * Check if a definition type has items
 */
function hasDefinitionItems(data: { items?: unknown[] } | null | undefined): boolean {
  return (data?.items?.length ?? 0) >= 1;
}

/**
 * Check if design/delivery has been created (has at least one version)
 */
function hasVersions(versions: unknown[] | null | undefined): boolean {
  // The hook returns a placeholder version when empty, so check for real versions
  if (!versions || versions.length === 0) return false;
  // If there's only one version and it's the placeholder, consider it empty
  const firstVersion = versions[0] as { id?: string };
  if (versions.length === 1 && firstVersion?.id === 'initial') return false;
  return true;
}

/**
 * Calculate phase completion status
 */
export function calculatePhaseStatus(data: {
  // Discovery
  goals: DiscoveryArtefact | null | undefined;
  interviews: DiscoveryArtefact | null | undefined;
  competitors: DiscoveryArtefact | null | undefined;
  // Definition
  personas: { items?: unknown[] } | null | undefined;
  useCases: { items?: unknown[] } | null | undefined;
  // Design
  architectureVersions: unknown[] | null | undefined;
  areasVersions: unknown[] | null | undefined;
  // Delivery
  techArchVersions: unknown[] | null | undefined;
  storymapVersions: unknown[] | null | undefined;
  mvpVersions: unknown[] | null | undefined;
}): PhaseStatus {
  // Discovery: all three must have at least 1 item
  const discoveryComplete = 
    hasItems(data.goals) && 
    hasItems(data.interviews) && 
    hasItems(data.competitors);
  
  // Definition: personas AND use-cases must have at least 1 item each
  const definitionComplete = 
    hasDefinitionItems(data.personas) && 
    hasDefinitionItems(data.useCases);
  
  // Design: architecture OR areas must have content (at least one)
  const designComplete = 
    hasVersions(data.architectureVersions) || 
    hasVersions(data.areasVersions);
  
  // Delivery: tech-arch, storymap, OR mvp must have content (at least one)
  const deliveryComplete = 
    hasVersions(data.techArchVersions) || 
    hasVersions(data.storymapVersions) || 
    hasVersions(data.mvpVersions);
  
  return {
    discovery: discoveryComplete,
    definition: definitionComplete,
    design: designComplete,
    delivery: deliveryComplete,
  };
}

/**
 * Check if a phase requirement is met
 */
export function isPhaseComplete(phase: Phase, status: PhaseStatus): boolean {
  switch (phase) {
    case 'discovery':
      return status.discovery;
    case 'definition':
      // Definition requires Discovery to be complete first
      return status.discovery && status.definition;
    case 'design':
      // Design requires Discovery + Definition to be complete
      return status.discovery && status.definition && status.design;
    case 'delivery':
      // Delivery requires all previous phases
      return status.discovery && status.definition && status.design && status.delivery;
    default:
      return false;
  }
}

// ============================================================================
// Hook: useNavigationGating
// ============================================================================

/**
 * Hook to manage navigation gating based on workflow completion
 * 
 * @param projectId - The project ID to check completion for
 * @returns Object with isItemDisabled, getBlockedTooltip, phaseStatus, isLoading
 */
export function useNavigationGating(projectId: string | undefined): NavigationGatingResult {
  // Fetch Discovery data
  const { data: goals, isLoading: goalsLoading } = useDiscovery(projectId || '', 'goals');
  const { data: interviews, isLoading: interviewsLoading } = useDiscovery(projectId || '', 'interviews');
  const { data: competitors, isLoading: competitorsLoading } = useDiscovery(projectId || '', 'competitors');
  
  // Fetch Definition data
  const { data: personas, isLoading: personasLoading } = useDefinition(projectId || '', 'personas');
  const { data: useCases, isLoading: useCasesLoading } = useDefinition(projectId || '', 'use-cases');
  
  // Fetch Design data (versions)
  const { data: architectureVersions, isLoading: archLoading } = useDesignVersionsRaw(projectId, 'architecture');
  const { data: areasVersions, isLoading: areasLoading } = useDesignVersionsRaw(projectId, 'areas');
  
  // Fetch Delivery data (versions)
  const { data: techArchVersions, isLoading: techArchLoading } = useDeliveryVersionsRaw(projectId, 'architecture');
  const { data: storymapVersions, isLoading: storymapLoading } = useDeliveryVersionsRaw(projectId, 'storymap');
  const { data: mvpVersions, isLoading: mvpLoading } = useDeliveryVersionsRaw(projectId, 'mvp');
  
  const isLoading = 
    goalsLoading || interviewsLoading || competitorsLoading ||
    personasLoading || useCasesLoading ||
    archLoading || areasLoading ||
    techArchLoading || storymapLoading || mvpLoading;
  
  // Calculate phase status
  const phaseStatus = useMemo(() => {
    // While loading, assume nothing is complete to prevent UI flickering
    if (isLoading) {
      return {
        discovery: false,
        definition: false,
        design: false,
        delivery: false,
      };
    }
    
    return calculatePhaseStatus({
      goals,
      interviews,
      competitors,
      personas,
      useCases,
      architectureVersions,
      areasVersions,
      techArchVersions,
      storymapVersions,
      mvpVersions,
    });
  }, [
    isLoading,
    goals, interviews, competitors,
    personas, useCases,
    architectureVersions, areasVersions,
    techArchVersions, storymapVersions, mvpVersions,
  ]);
  
  // Check if an item is disabled due to gating
  const isItemDisabled = useMemo(() => {
    return (itemId: string): boolean => {
      const rule = NAVIGATION_GATING[itemId];
      
      // No rule or no required phase = always accessible
      if (!rule || rule.requiredPhase === null) {
        return false;
      }
      
      // Check if the required phase is complete
      const requiredPhase = rule.requiredPhase;
      
      switch (requiredPhase) {
        case 'discovery':
          return !phaseStatus.discovery;
        case 'definition':
          return !phaseStatus.discovery || !phaseStatus.definition;
        case 'design':
          return !phaseStatus.discovery || !phaseStatus.definition || !phaseStatus.design;
        case 'delivery':
          return !phaseStatus.discovery || !phaseStatus.definition || !phaseStatus.design || !phaseStatus.delivery;
        default:
          return false;
      }
    };
  }, [phaseStatus]);
  
  // Get blocked tooltip message
  const getBlockedTooltip = useMemo(() => {
    return (itemId: string): string => {
      const rule = NAVIGATION_GATING[itemId];
      if (!rule || !isItemDisabled(itemId)) {
        return '';
      }
      return rule.tooltipWhenBlocked;
    };
  }, [isItemDisabled]);
  
  return {
    isItemDisabled,
    getBlockedTooltip,
    phaseStatus,
    isLoading,
  };
}
